import { Component } from '@theme/component';

/**
 * @typedef {Object} ReadMoreRefs
 * @property {HTMLElement} content - Collapsible richtext container
 * @property {HTMLButtonElement} toggleButton - Read more / read less control
 */

/**
 * Collapses long richtext visually while keeping full content in the DOM for SEO.
 *
 * @extends {Component<ReadMoreRefs>}
 */
class ReadMoreComponent extends Component {
  requiredRefs = ['content', 'toggleButton'];

  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#measureAndUpdate();
    this.#resizeObserver = new ResizeObserver(() => this.#measureAndUpdate());
    this.#resizeObserver.observe(this.refs.content);
  }

  disconnectedCallback() {
    this.#resizeObserver?.disconnect();
  }

  /**
   * @returns {number}
   */
  #getCollapsedLines() {
    const lines = parseInt(this.dataset.collapsedLines ?? '6', 10);
    return Number.isFinite(lines) && lines > 0 ? lines : 6;
  }

  /**
   * @returns {number}
   */
  #getCollapsedHeight() {
    const { content } = this.refs;
    const style = getComputedStyle(content);
    const lineHeight = parseFloat(style.lineHeight);

    if (!Number.isFinite(lineHeight) || lineHeight <= 0) {
      return content.clientHeight;
    }

    return lineHeight * this.#getCollapsedLines();
  }

  #measureAndUpdate = () => {
    const { content, toggleButton } = this.refs;
    const collapsedHeight = this.#getCollapsedHeight();

    this.style.setProperty('--read-more-collapsed-height', `${collapsedHeight}px`);

    const previousMaxHeight = content.style.maxHeight;
    content.style.maxHeight = 'none';
    const fullHeight = content.scrollHeight;
    content.style.maxHeight = previousMaxHeight;

    this.style.setProperty('--read-more-expanded-height', `${fullHeight}px`);

    const overflows = fullHeight > collapsedHeight + 1;

    if (!overflows) {
      this.dataset.noClamp = 'true';
      toggleButton.hidden = true;
      this.dataset.expanded = 'true';
      toggleButton.setAttribute('aria-expanded', 'true');
      return;
    }

    delete this.dataset.noClamp;
    toggleButton.hidden = false;

    const isExpanded = this.dataset.expanded === 'true';
    toggleButton.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
  };

  /**
   * @param {Event} event
   */
  toggle = (event) => {
    event.preventDefault();

    if (this.dataset.noClamp === 'true') return;

    const { toggleButton } = this.refs;
    const expanded = this.dataset.expanded === 'true';

    this.dataset.expanded = expanded ? 'false' : 'true';
    toggleButton.setAttribute('aria-expanded', this.dataset.expanded);
  };
}

if (!customElements.get('read-more-component')) {
  customElements.define('read-more-component', ReadMoreComponent);
}
